import type { AudienceSegmentCardVO } from "@trex-2b/api/src/trex-api/trex-api";

/** Onboarding chat 消息角色与步骤组件标识（与文案配置解耦，供 types 层复用）。 */
export const ONBOARDING_CHAT_ROLE = {
  USER: "user",
  AI: "ai",
} as const;

export type OnboardingChatRole =
  (typeof ONBOARDING_CHAT_ROLE)[keyof typeof ONBOARDING_CHAT_ROLE];

export const ONBOARDING_CHAT_COMPONENT = {
  WEB_SCAN: "web_scan",
  REPORT_SUMMARY: "report_summary",
  AUDIENCE: "audience_selection",
  PRICING: "pricing_tiers",
  BUDGET: "budget_selection",
  ENTERPRISE: "enterprise_contact",
  DRAFT_SAVED: "draft_saved_card",
  PAYMENT_SUCCESS: "payment_success_card",
  /** 草稿回填：先在时间线中选「回到哪一步」（未纳入 URL 锚点映射） */
  DRAFT_RETURN_STEP_PICK: "draft_return_step_pick",
} as const;

export type OnboardingChatComponent =
  (typeof ONBOARDING_CHAT_COMPONENT)[keyof typeof ONBOARDING_CHAT_COMPONENT];

/** 步骤内容区进行中回调，驱动对应 AI 消息顶栏 PRISM Analysis 加载态 */
export type TOnboardingStepContentActiveChange = (
  component: OnboardingChatComponent,
  isStepContentActive: boolean,
) => void;

/** 步骤组件 payload（各 step 结构不同，统一为可 JSON 序列化的对象） */
export type OnboardingChatMessageData = Record<string, any>;

/**
 * PRISM onboarding 聊天时间线单条消息（用户气泡 / AI 文案 + 可选步骤块）。
 */
export type OnboardingChatMessage = {
  id: string;
  role: OnboardingChatRole;
  text?: string;
  guideText?: string;
  isTyping?: boolean;
  /** 与 `ONBOARDING_CHAT_COMPONENT` 取值一致；兼容持久化 JSON 中的 string */
  component?: OnboardingChatComponent | string;
  data?: OnboardingChatMessageData;
  showComponent?: boolean;
  hideAction?: boolean;
};

export type OnboardingChatMessageUpdate = Partial<OnboardingChatMessage>;

/** PRISM 五维度标识 */
export type TDimensionKey = "D1" | "D2" | "D3" | "D4" | "D5";

/** 五维展示顺序，与 Step2 时间线自上而下一致。 */
export const DIMENSION_ORDER: TDimensionKey[] = ["D1", "D2", "D3", "D4", "D5"];

/** D3–D5 追问维度（confirm_needed 后续作答） */
export type TFollowupDimensionKey = Extract<TDimensionKey, "D3" | "D4" | "D5">;

/** 单维追问作答结果（completion 驱动折叠/展开） */
export type TFollowupCompletion = "none" | "answered" | "skipped";

export type TFollowupDimensionRecord = {
  completion: TFollowupCompletion;
  selectedValue?: string;
};

/** Mode B 单维追问展示配置 */
export type TFollowupDimensionConfig = {
  title: string;
  questionText: string;
  options: { value: string; label: string }[];
};

/** SSE `data-prism-followup-config` 分片 payload */
export type TPrismFollowupConfigStreamPayload = {
  dimensionConfigsByKey: Partial<
    Record<TFollowupDimensionKey, TFollowupDimensionConfig>
  >;
};

/** SSE `data-audience` 分片 payload，与 `parseScanStream` 约定一致 */
export type TPrismAudienceStreamPayload = {
  audienceSegments: AudienceSegmentCardVO[];
  personas: string[];
};

/** PRISM analyze 报告模式：A 全 verified / B 部分追问 / C 无信号兜底 */
export type TPrismReportMode = "A" | "B" | "C";

/** analyze 接口原始 JSON（字段名以后端为准，解析前不做强类型约束）。 */
export type TPrismAnalyzePayload = Record<string, unknown>;

/** SSE 终态：done → 检索完成；confirm_needed → "Confirm needed" */
export type TDimensionStatus = "done" | "confirm_needed";

/** 单维度展示数据；未带 status 表示该维仍在流式推送中 */
export type TDimensionRecord = {
  dimensionKey: TDimensionKey;
  dimensionName: string;
  narrativeDescription: string;
  status?: TDimensionStatus;
  dataTags?: string[];
};

/** SSE `dimension` 增量事件：每条均含 dimensionName、narrativeDescription；status / dataTags 分步补齐 */
export type TDimensionStreamPayload = {
  dimensionKey: TDimensionKey;
  dimensionName: string;
  narrativeDescription: string;
  status?: TDimensionStatus;
  dataTags?: string[];
};

/**
 * 单维聚合结果：供 BFF `/api/prism-stream` 生成 `data-dimension` chunk。
 * `dataTags` 在维内存在 neutral/positive tag 时，从 profile 采集字段格式化展示。
 */
export type TPrismAggregatedDimensionRecord = {
  dimensionKey: TDimensionKey;
  dimensionName: string;
  narrativeDescription: string;
  dataTags?: string[];
  finalStatus: TDimensionStatus;
};

/** analyze `data_sources_hit` 单条命中，映射为 Web Search 列表行 */
export type TWebSearchSource = {
  title: string;
  /** 数据源域名，对应 API `host` */
  domain: string;
  /** 数据源名称，对应 API `name`（列表右侧优先展示） */
  name?: string;
  /** 命中摘要，对应 API `detail` */
  detail?: string;
};

/** SSE `data-data_source` 分片 payload，对应 WebSearchCard 展示字段 */
export type TWebSearchStreamPayload = {
  queryText: string;
  summaryText?: string;
  sources: TWebSearchSource[];
  /** 模式C可覆盖顶部结果数（例如：有诊断行但实际 0 命中） */
  resultsCountOverride?: number;
  mode?: "default" | "no_signal";
};

type TPrismScanRunHistoryEntry = {
  webSearchPayload: TWebSearchStreamPayload | null;
  dimensions: TDimensionRecord[];
  showModeCRetryCard?: boolean;
  retryCardHandle?: string;
};

/** 写入 WEB_SCAN 消息 `data.prismScanSnapshot`，随 onboarding session 持久化 */
export type TPrismScanSessionSnapshot = {
  webSearchPayload: TWebSearchStreamPayload | null;
  dimensions: TDimensionRecord[];
  scanRunHistory?: TPrismScanRunHistoryEntry[];
  hasAttemptedRescanOrSkip?: boolean;
  isFollowupEditLocked?: boolean;
  fallbackFormValues?: {
    projectType: string;
    goal: string;
  };
  /** Mode C Try again 后的最新 X handle，供 report 步骤与 draft 恢复读取 */
  scanHandle?: string;
  /** Mode B 已作答追问（刷新后还原收起态摘要） */
  followupRecords?: Partial<
    Record<TFollowupDimensionKey, TFollowupDimensionRecord>
  >;
};
